// Guest mode on Firebase anonymous auth, with Google and email linked to
// the same account so nothing is lost. A port of FirebaseAuthRepository.
// Apple waits for an Apple Services ID, as on the phone app's web build.
import {
  AuthErrorCodes,
  type AuthCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { collection, deleteDoc, doc, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { auth, firestore } from '@/data/firebase';

export interface AuthUser {
  uid: string;
  isGuest: boolean;
  email: string | null;
}

export type SignInMethod = 'google' | 'email';

export type AuthError =
  | 'invalidEmail'
  | 'weakPassword'
  | 'wrongCredentials'
  | 'userDisabled'
  | 'tooManyRequests'
  | 'noInternet'
  | 'methodNotEnabled'
  | 'recentLoginNeeded'
  | 'signInFailed'
  | 'emailTakenWrongPassword'
  | 'popupBlocked';

/** A sign-in that failed, for the screen to put in words. */
export class AuthFailure extends Error {
  constructor(readonly reason: AuthError) {
    super(reason);
  }
}

/** The account is already in use: the person can sign in to it instead. */
export class AccountExists extends Error {
  constructor(readonly method: SignInMethod) {
    super('account-exists');
  }
}

export function authErrorFor(code: string): AuthError {
  switch (code) {
    case 'auth/invalid-email':
      return 'invalidEmail';
    case 'auth/weak-password':
      return 'weakPassword';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'wrongCredentials';
    case 'auth/user-disabled':
      return 'userDisabled';
    case 'auth/too-many-requests':
      return 'tooManyRequests';
    case 'auth/network-request-failed':
      return 'noInternet';
    case 'auth/operation-not-allowed':
      return 'methodNotEnabled';
    case 'auth/requires-recent-login':
      return 'recentLoginNeeded';
    case 'auth/popup-blocked':
      return 'popupBlocked';
    default:
      return 'signInFailed';
  }
}

const toUser = (user: User | null): AuthUser | null => (user ? { uid: user.uid, isGuest: user.isAnonymous, email: user.email } : null);
const codeOf = (e: unknown) => (e instanceof FirebaseError ? e.code : '');
const isCancel = (code: string) => code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request' || code === 'auth/user-cancelled';

// What "sign in instead" uses after an AccountExists; never kept longer.
let pendingCredential: AuthCredential | null = null;
let pendingEmail: { email: string; password: string } | null = null;

// Linking an account changes the user without signing in again, which
// onAuthStateChanged doesn't report, so those changes are passed on here.
const listeners = new Set<(user: AuthUser | null) => void>();
const changed = () => listeners.forEach((l) => l(toUser(auth().currentUser)));

export function watchUser(onChange: (user: AuthUser | null) => void): () => void {
  listeners.add(onChange);
  const stop = onAuthStateChanged(auth(), (user) => onChange(toUser(user)));
  return () => {
    listeners.delete(onChange);
    stop();
  };
}

export const currentUser = () => toUser(auth().currentUser);

/** Waits for the saved sign-in, then signs in as a guest if there is none. */
export async function ensureSignedIn(): Promise<AuthUser> {
  await auth().authStateReady();
  const existing = auth().currentUser;
  if (existing) {
    touch(existing, false);
    return toUser(existing)!;
  }
  const { user } = await signInAnonymously(auth());
  touch(user, true);
  return toUser(user)!;
}

/** Links Google to the guest account in a popup. False when cancelled. */
export async function linkGoogle(): Promise<boolean> {
  const user = auth().currentUser;
  if (!user) throw new AuthFailure('signInFailed');
  try {
    await linkWithPopup(user, new GoogleAuthProvider());
    touch(user, false);
    changed();
    return true;
  } catch (e) {
    const code = codeOf(e);
    if (isCancel(code)) return false;
    if (code === AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE || code === AuthErrorCodes.EMAIL_EXISTS || code === 'auth/account-exists-with-different-credential') {
      pendingCredential = GoogleAuthProvider.credentialFromError(e as FirebaseError);
      pendingEmail = null;
      if (!pendingCredential) throw new AuthFailure('signInFailed');
      throw new AccountExists('google');
    }
    if (code === 'auth/provider-already-linked') return true;
    throw new AuthFailure(authErrorFor(code));
  }
}

/** Links an email and password to the guest account, or signs in with them. */
export async function continueWithEmail(email: string, password: string): Promise<void> {
  const user = auth().currentUser;
  try {
    if (!user) {
      await signInOrCreate(email, password);
      return;
    }
    await linkWithCredential(user, EmailAuthProvider.credential(email, password));
    touch(user, false);
    changed();
  } catch (e) {
    if (e instanceof AuthFailure) throw e;
    const code = codeOf(e);
    if (code === AuthErrorCodes.EMAIL_EXISTS || code === AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE) {
      pendingEmail = { email, password };
      pendingCredential = null;
      throw new AccountExists('email');
    }
    throw new AuthFailure(authErrorFor(code));
  }
}

async function signInOrCreate(email: string, password: string) {
  try {
    await signInWithEmailAndPassword(auth(), email, password);
  } catch (e) {
    const code = codeOf(e);
    if (code !== 'auth/user-not-found' && code !== 'auth/invalid-credential' && code !== 'auth/invalid-login-credentials') throw e;
    try {
      const created = await createUserWithEmailAndPassword(auth(), email, password);
      touch(created.user, true);
    } catch (e2) {
      // The account exists, so the first failure was a wrong password.
      throw new AuthFailure(codeOf(e2) === AuthErrorCodes.EMAIL_EXISTS ? 'wrongCredentials' : authErrorFor(codeOf(e2)));
    }
  }
}

/** Leaves the guest account for the existing one the person chose. */
export async function signInInstead(): Promise<void> {
  try {
    if (pendingCredential) await signInWithCredential(auth(), pendingCredential);
    else if (pendingEmail) await signInWithEmailAndPassword(auth(), pendingEmail.email, pendingEmail.password);
    else throw new AuthFailure('signInFailed');
    touch(auth().currentUser!, false);
  } catch (e) {
    if (e instanceof AuthFailure) throw e;
    const reason = authErrorFor(codeOf(e));
    // The email has an account, but not with this password.
    if (pendingEmail && reason === 'wrongCredentials') throw new AuthFailure('emailTakenWrongPassword');
    throw new AuthFailure(reason);
  } finally {
    forgetPendingSignIn();
  }
}

export function forgetPendingSignIn() {
  pendingCredential = null;
  pendingEmail = null;
}

export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth(), email.trim());
  } catch (e) {
    throw new AuthFailure(authErrorFor(codeOf(e)));
  }
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth());
}

/** Deleting needs a recent sign-in, except for a guest. */
export function canDeleteAccountNow(): boolean {
  const user = auth().currentUser;
  if (!user || user.isAnonymous) return true;
  const last = user.metadata.lastSignInTime;
  return last != null && Date.now() - Date.parse(last) < 5 * 60_000;
}

/** Every collection under users/{uid} (firestore.rules lists the same). */
const USER_COLLECTIONS = ['businesses', 'clients', 'invoices', 'items'];

/**
 * Everything the account holds, before the account itself: Firestore keeps
 * a user's businesses, clients, invoices and saved items when users/{uid} is
 * deleted, so each is deleted, and with them the public pages its invoices
 * published. As the phone app's FirebaseAuthRepository does.
 */
async function deleteData(uid: string) {
  const db = firestore();
  const invoices = await getDocs(collection(db, 'users', uid, 'invoices'));
  for (const invoice of invoices.docs) {
    const pageId = invoice.data().publicLinkId;
    if (typeof pageId === 'string' && pageId) {
      // Already gone, or never published: nothing to delete.
      await deleteDoc(doc(db, 'publicInvoices', pageId)).catch((e) => console.warn('Could not delete a public invoice page', e));
    }
  }
  for (const name of USER_COLLECTIONS) {
    const docs = name === 'invoices' ? invoices.docs : (await getDocs(collection(db, 'users', uid, name))).docs;
    // Batches stay under Firestore's 500 writes each.
    for (let i = 0; i < docs.length; i += 400) {
      const batch = writeBatch(db);
      for (const d of docs.slice(i, i + 400)) batch.delete(d.ref);
      await batch.commit();
    }
  }
}

export async function deleteAccount(): Promise<void> {
  const user = auth().currentUser;
  if (!user) return;
  try {
    await deleteData(user.uid);
    await deleteDoc(doc(firestore(), 'users', user.uid));
    await user.delete();
  } catch (e) {
    throw new AuthFailure(authErrorFor(codeOf(e)));
  }
}

/**
 * Keeps users/{uid} current (guest or not, last active) without waiting for
 * the server; `plan` is written only by server code.
 */
function touch(user: User, isNew: boolean) {
  const now = new Date().toISOString();
  setDoc(doc(firestore(), 'users', user.uid), { isGuest: user.isAnonymous, lastActiveAt: now, ...(isNew ? { createdAt: now } : {}) }, { merge: true }).catch((e) =>
    console.warn('Could not update the user record', e),
  );
}
